import type { operations } from '@multaqa/api-client'
import type { Condition, MatchMode } from '@/components/condition-filter'

/**
 * The register's own vocabularies, taken from the generated contract rather
 * than copied by hand: if the API stops accepting `void`, the select that
 * offers it stops compiling.
 */
type JournalEntryIndexQuery = NonNullable<operations['journalEntry.index']['parameters']['query']>
export type JournalEntryStatus = NonNullable<JournalEntryIndexQuery['status']>
export type JournalEntryType = NonNullable<JournalEntryIndexQuery['entry_type']>
export const JOURNAL_ENTRY_STATUSES = [
  'draft',
  'posted',
  'void',
] as const satisfies readonly JournalEntryStatus[]
export const JOURNAL_ENTRY_TYPES = [
  'manual',
  'document',
  'opening',
  'reversal',
  'closing',
  'depreciation',
  'revaluation',
  'payroll',
] as const satisfies readonly JournalEntryType[]

/** Which register column a sort param addresses. */
export type JournalSortColumn = 'date' | 'amount'

/**
 * The journal register's filters, owned by the route's URL search params and
 * applied by the API.
 *
 * A filter that only changed local state would answer about the page already
 * loaded; these name the URL so a view is linkable and reload-surviving, and
 * `useJournalEntries` forwards them to the server.
 */
export interface JournalEntryFilters {
  conditions?: Condition[]
  match?: MatchMode
  c?: string
  /** Free text over entry number and description. */
  q?: string
  /** A normalised source slug (`invoice`, `payroll_run`) or `manual`. */
  source?: string
  journal_ids?: string[]
  from?: string
  to?: string
  status?: JournalEntryStatus
  /** An account id any line touches. */
  account?: string
  entryType?: JournalEntryType
  /** A cost center id (= a department) any line touches. */
  costCenter?: string
  adjusting?: boolean
  /** Bounds on the entry's total debit, as exact decimal strings. */
  minAmount?: string
  maxAmount?: string
  sort?: JournalSortColumn
  dir?: 'asc' | 'desc'
  page?: number
  perPage?: number
}

export interface JournalLineBody {
  account: string
  debit?: string | null
  credit?: string | null
  description?: string | null
  currency?: string
  cost_center_id?: string | null
  party_type?: 'customer' | 'supplier' | 'employee' | null
  party_id?: string | null
}

export interface JournalEntryBody {
  entry_date: string
  journal_id?: string | null
  entry_type?: string
  description?: string | null
  analysis_tag_ids?: string[]
  lines: JournalLineBody[]
}
